import { create } from "zustand";
import { cookieService } from "@/lib/cookie/cookieService";

const getCookieDomain = () => {
  if (typeof window === "undefined") return undefined;
  
  const hostname = window.location.hostname;
  
  if (hostname.endsWith(".dev.togetherwork.vn")) {
    return ".dev.togetherwork.vn";
  }
  
  return undefined;
};

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
}

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  expired: number | null;
  user: User | null;
}

interface AuthActions {
  setToken: (payload: { token: string; expired: number } | null) => void;
  setRefreshToken: (payload: { token: string; expired: number } | null) => void;
  setExpired: (expired: number | null) => void;
  setUser: (user: User | null) => void;
  logoutUser: () => void;
}

export type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>((set) => ({
  token: null,
  refreshToken: null,
  expired: null,
  user: null,

  setToken: (payload) => {
    if (payload) {
      cookieService.set("token", payload.token, {
        encrypt: true,
        expires: payload.expired,
        domain: getCookieDomain(),
      });
      set({ token: payload.token });
    } else {
      cookieService.remove("token", { domain: getCookieDomain() });
      set({ token: null });
    }
  },
  setRefreshToken: (payload) => {
    if (payload) {
      cookieService.set("refreshToken", payload.token, {
        encrypt: true,
        expires: payload.expired,
        domain: getCookieDomain(),
      });
      set({ refreshToken: payload.token });
    } else {
      cookieService.remove("refreshToken", { domain: getCookieDomain() });
      set({ refreshToken: null });
    }
  },
  setExpired: (expired) => set({ expired }),
  setUser: (user) => set({ user }),
  logoutUser: () => {
    cookieService.remove("token", { domain: getCookieDomain() });
    cookieService.remove("refreshToken", { domain: getCookieDomain() });
    cookieService.remove("businessId", { domain: getCookieDomain() });
    set({
      token: null,
      refreshToken: null,
      expired: null,
      user: null,
    });
  },
}));

export const useGetToken = () => {
  const token = useAuthStore((state) => state.token);
  return token ? token : cookieService.get("token");
};
